// ===============================================================
// 🛑 BlockListPage.js — قائمة الحظر (Livora)
// - uses: useLivoraToast + useLivoraDialog + LivoraToast + LivoraModalCard
// ===============================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./BlockListPage.css";

import { auth, db, functions, storage } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, getDownloadURL } from "firebase/storage";

import TopBar from "../components/common/TopBar";
import { useTranslation } from "react-i18next";

import useLivoraToast from "../hooks/useLivoraToast";
import useLivoraDialog from "../hooks/useLivoraDialog";
import LivoraToast from "../components/common/LivoraToast";
import LivoraModalCard from "../components/common/LivoraModalCard";
import { defaultAvatarByGender } from "../utils/defaultAvatar";

function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

async function resolveImageUrl(value) {
  if (!value || typeof value !== "string") return "";

  if (isHttpUrl(value)) return value;

  try {
    return await getDownloadURL(ref(storage, value));
  } catch (e) {
    console.error("resolveImageUrl failed:", value, e);
    return "";
  }
}

function BlockListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const { toast, showToast } = useLivoraToast();
  const {
    dialog,
    openDialog,
    closeDialog,
    forceCloseDialog,
    setDialogLoading,
  } = useLivoraDialog();

  // ===============================================================
  // 📌 تحميل قائمة الحظر من collection blocks
  // ===============================================================
  useEffect(() => {
    let isMounted = true;

    const loadBlockedList = async () => {
      const user = auth.currentUser;
      if (!user) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, "blocks"),
          where("blockerUid", "==", user.uid)
        );

        const snap = await getDocs(q);

        const rows = await Promise.all(
          snap.docs.map(async (d) => {
            const data = d.data() || {};
            const blockedUid = data.blockedUid || "";

            let blockedUser = {
              uid: blockedUid,
              name: t("unknown_user", { defaultValue: "مستخدم" }),
              avatar: "",
              gender: "",
            };

            if (blockedUid) {
              try {
                const userSnap = await getDoc(
                  doc(db, "publicProfiles", blockedUid)
                );

                if (userSnap.exists()) {
                  const u = userSnap.data() || {};

                  const rawAvatar =
                    u.photoURL ||
                    u.avatar ||
                    u.profileImage ||
                    u.mainPhoto ||
                    "";

                  const avatarUrl = await resolveImageUrl(rawAvatar);

                  blockedUser = {
                    uid: blockedUid,
                    name:
                      u.name ||
                      u.displayName ||
                      u.username ||
                      t("unknown_user", { defaultValue: "مستخدم" }),
                    avatar: avatarUrl,
                    gender: u.gender || "",
                  };
                }
              } catch (e) {
                console.error(
                  "Failed to load blocked user profile:",
                  blockedUid,
                  e
                );
              }
            }

            return {
              id: d.id,
              blockedUid,
              user: blockedUser,
            };
          })
        );

        if (isMounted) {
          setBlockedUsers(rows);
        }
      } catch (error) {
        console.error("❌ loadBlockedList error:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBlockedList();

    return () => {
      isMounted = false;
    };
  }, [t]);

  // ===============================================================
  // 🔥 فتح بطاقة فك الحظر
  // ===============================================================
  const openUnblockCard = (blockedUid) => {
    if (!blockedUid || busyId) return;

    openDialog({
      title: t("unblock", { defaultValue: "إزالة الحظر" }),
      message: t("blocklist_unblock_confirm", {
        defaultValue: "هل أنت متأكد أنك تريد إزالة الحظر عن هذا المستخدم؟",
      }),
      confirmText: t("unblock", { defaultValue: "إزالة الحظر" }),
      cancelText: t("cancel", { defaultValue: "إلغاء" }),
      variant: "default",
      onConfirm: async () => {
        const user = auth.currentUser;
        if (!user) return;

        try {
          setBusyId(blockedUid);
          setDialogLoading(true);

          const unblockUserFn = httpsCallable(functions, "unblockUser");
          await unblockUserFn({ blockedUid });

          setBlockedUsers((prev) =>
            prev.filter((item) => item.blockedUid !== blockedUid)
          );

          forceCloseDialog();

          showToast(
            "success",
            t("unblock_success", {
              defaultValue: "تم فك الحظر بنجاح",
            })
          );
        } catch (error) {
          console.error("❌ Unblock error:", error);
          setDialogLoading(false);

          showToast(
            "error",
            t("general_error", {
              defaultValue: "حدث خطأ",
            })
          );
        } finally {
          setBusyId("");
        }
      },
    });
  };

  return (
    <div className="blocklist-root">
      <TopBar
        title={t("block_list", { defaultValue: "قائمة الحظر" })}
        onBack={() => navigate(-1)}
      />

      <LivoraToast toast={toast} />
      <LivoraModalCard dialog={dialog} onClose={closeDialog} />

      <div className="blocklist-content">
        {loading ? (
          <p className="loading-text">
            {t("loading", { defaultValue: "جارٍ التحميل..." })}
          </p>
        ) : blockedUsers.length === 0 ? (
          <p className="empty-text">
            {t("block_list_empty", {
              defaultValue: "لا يوجد مستخدمون محظورون",
            })}
          </p>
        ) : (
          blockedUsers.map((item) => (
            <div key={item.id} className="blocked-item">
              <div className="left">
                <img
                  src={
                    item.user?.avatar ||
                    defaultAvatarByGender(item.user?.gender)
                  }
                  alt={item.user.name}
                  className="blocked-avatar"
                  onError={(e) => {
                    e.currentTarget.src = defaultAvatarByGender(
                      item.user?.gender
                    );
                  }}
                />

                <div className="blocked-meta">
                  <span className="blocked-name">
                    {item.user?.name ||
                      t("unknown_user", { defaultValue: "مستخدم" })}
                  </span>
                </div>
              </div>

              <button
                className="unblock-btn"
                disabled={busyId === item.blockedUid}
                onClick={() => openUnblockCard(item.blockedUid)}
              >
                {busyId === item.blockedUid
                  ? t("loading", { defaultValue: "جارٍ التحميل..." })
                  : t("unblock", { defaultValue: "إزالة الحظر" })}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default BlockListPage;  